import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TabularExtractionMode } from '@/services/api/cases'
import { isTableFileType } from '@/utils/table-files'

export interface TableUploadProcessing {
  value: TabularExtractionMode
  onChange: (mode: TabularExtractionMode) => void
  onFileChange: (id: string, mode: TabularExtractionMode | undefined) => void
}

interface StagedTableProcessingProps {
  file: {
    id: string
    file: { name: string }
    status: string
    relativePath?: string | null
    tableMode?: TabularExtractionMode
  }
  processing: TableUploadProcessing
  disabled?: boolean
}

export function StagedTableProcessing({ file, processing, disabled }: StagedTableProcessingProps) {
  const { t } = useTranslation()
  if (!isTableFileType(file.file.name.split('.').pop())) return null
  const labels = {
    structured: t('structuredIngest.processing.upload.structured'),
    llm: t('structuredIngest.processing.upload.llm'),
    inherit: t('structuredIngest.processing.upload.inherit', {
      mode: t(`structuredIngest.processing.upload.${processing.value}`),
    }),
  }
  return (
    <Select
      value={file.tableMode ?? 'inherit'}
      disabled={disabled || file.status === 'uploading' || file.status === 'uploaded'}
      onValueChange={mode => {
        if (mode === 'inherit' || mode === 'structured' || mode === 'llm')
          processing.onFileChange(file.id, mode === 'inherit' ? undefined : mode)
      }}
    >
      <SelectTrigger
        size="sm"
        className="mt-2 max-w-full text-xs"
        aria-label={t('structuredIngest.processing.upload.fileLabel', {
          filename: file.relativePath || file.file.name,
        })}
      >
        <SelectValue>{labels[file.tableMode ?? 'inherit']}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {(['inherit', 'structured', 'llm'] as const).map(mode => (
          <SelectItem key={mode} value={mode}>
            {labels[mode]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
